/* PrimeCut collection page: filter/sort auto-submit, filter popovers, and the quick-add sheet.
   Quick add: single tees choose color and size; preset packs show their fixed colors and choose one size, with nothing
   preselected. "Size Guide" switches the same sheet to the inches-first size guide and back without losing the choice.
   A valid choice adds the real variant through /cart/add.js (component bundles expand in Shopify), refreshes the theme
   cart drawer and count, and keeps the shopper on the collection. Draft packs listed by the SHUR ONLY preview adapter
   never add to the cart: they show a clearly marked review state instead.
   Gift-eligible bundles add together with their free gifts in the same request Kaching Bundles' product-page widget
   sends (see buildGiftItems), so Kaching's deal discount prices the gifts exactly as it does on the product page. */
(() => {
  'use strict';

  /* Kaching Bundles only discounts a gift line that carries its __kaching_bundles property, linked by a shared group id
     to a bundle line marked "main". The ids it needs (deal, deal bar, gifts, version) belong to Kaching and change when
     the deal is edited, so they are read from the bundle's own product page, where Kaching renders the deal it applies
     to that product — never hard-coded here. One page read per bundle, started when the sheet opens.
     Gift rules: the $10 gift card comes with every bundle; any other gift (the e-book) is added only when the cart
     does not already hold a promotional one, so a cart gets one e-book however many bundles it has.
     assets/pc-cart-gift.js keeps both rules true as quantities change afterwards. */
  const GIFT_CARD_HANDLE = 'primecut-gift-card';
  const dealCache = new Map();
  const readJson = (el) => {
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };
  const loadDeal = (url) => {
    if (!dealCache.has(url)) {
      const request = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return {
            products: [...doc.querySelectorAll('script.kaching-bundles-product')].map(readJson).filter(Boolean),
            deals: [...doc.querySelectorAll('script.kaching-bundles-deal-block-settings')].map(readJson).filter(Boolean),
          };
        });
      request.catch(() => dealCache.delete(url)); // a failed read is retried on the next attempt
      dealCache.set(url, request);
    }
    return dealCache.get(url);
  };
  const kachingGift = (item) => {
    const raw = item && item.properties && item.properties.__kaching_bundles;
    if (!raw) return null;
    try {
      const data = JSON.parse(raw);
      return data && data.gift ? data : null;
    } catch (e) {
      return null;
    }
  };
  const numericId = (gid) => Number(String(gid).split('/').pop());
  const groupId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const bytes = new Uint8Array(4);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    return [...bytes].map((b) => chars[b % chars.length]).join('');
  };

  // The /cart/add.js items for one bundle plus its gifts, or null when the deal can't be read with certainty.
  const buildGiftItems = (data, variantId, cart) => {
    const main = data.products.find((p) => (p.variants || []).some((v) => v.id === variantId));
    if (!main) return null;
    const withGifts = data.deals.filter((d) => (d.dealBars || []).some((b) => b.freeGifts && b.freeGifts.length));
    const deal =
      withGifts.find((d) => Array.isArray(d.selectedProductIds) && d.selectedProductIds.includes(main.id)) ||
      (withGifts.length === 1 ? withGifts[0] : null);
    if (!deal || !deal.nanoId) return null;
    const bars = deal.dealBars || [];
    const bar = bars.find((b) => b.id === deal.preselectedDealBarId) || bars.find((b) => b.freeGifts && b.freeGifts.length);
    // Only a one-unit bar matches a one-bundle add; anything else is left to the product page.
    if (!bar || !bar.freeGifts || !bar.freeGifts.length || bar.quantity !== 1) return null;
    const group = groupId();
    const common = deal.versionId ? { deal: deal.nanoId, v: deal.versionId } : { deal: deal.nanoId };
    const items = [];
    for (const gift of bar.freeGifts) {
      const productId = numericId(gift.productGID);
      const giftProduct = data.products.find((p) => p.id === productId);
      if (!giftProduct) return null;
      const variant =
        gift.variantGIDs && gift.variantGIDs.length
          ? (giftProduct.variants || []).find((v) => v.id === numericId(gift.variantGIDs[0]))
          : (giftProduct.variants || []).find((v) => v.availableForSale);
      if (!variant || !variant.availableForSale) return null;
      const oncePerCart = giftProduct.handle !== GIFT_CARD_HANDLE;
      if (oncePerCart && cart.items.some((item) => item.product_id === productId && kachingGift(item))) continue;
      items.push({
        id: variant.id,
        quantity: gift.quantity || 1,
        properties: { __kaching_bundles: JSON.stringify({ id: group, ...common, gift: gift.id }) },
      });
    }
    items.push({
      id: variantId,
      quantity: 1,
      properties: { __kaching_bundles: JSON.stringify({ ...common, pp: main.id, main: true, id: group, bid: bar.id }) },
    });
    return items;
  };

  const money = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };
  const rootUrl = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function initControls(root) {
    const form = root.querySelector('[data-pcc-form]');
    if (!form || form.dataset.pccReady) return;
    form.dataset.pccReady = 'true';
    const results = root.querySelector('.pcc__results');
    const submit = () => {
      if (results) results.classList.add('is-loading');
      const params = new URLSearchParams(new FormData(form));
      [...params.keys()].forEach((key) => {
        if (params.get(key) === '') params.delete(key);
      });
      const query = params.toString();
      window.location.assign(form.action + (query ? `?${query}` : ''));
    };
    form.addEventListener('change', (event) => {
      if (event.target.matches('input[type="number"]')) return;
      submit();
    });
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          submit();
        }
      });
      input.addEventListener('blur', () => {
        if (input.value !== input.defaultValue) submit();
      });
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      submit();
    });
    const filters = [...form.querySelectorAll('[data-pcc-filter]')];
    filters.forEach((details) =>
      details.addEventListener('toggle', () => {
        if (details.open) filters.forEach((other) => other !== details && (other.open = false));
      })
    );
    document.addEventListener('click', (event) => {
      filters.forEach((details) => {
        if (details.open && !details.contains(event.target)) details.open = false;
      });
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      filters.forEach((details) => {
        if (details.open) {
          details.open = false;
          details.querySelector('summary').focus();
        }
      });
    });
  }

  function initSheet(root) {
    const sheet = root.querySelector('[data-pcc-sheet]');
    if (!sheet || sheet.dataset.pccReady) return;
    sheet.dataset.pccReady = 'true';
    const q = (sel) => sheet.querySelector(sel);
    const title = q('[data-pcc-qs-title]');
    const img = q('[data-pcc-qs-img]');
    const summary = q('[data-pcc-qs-summary]');
    const price = q('[data-pcc-qs-price]');
    const contents = q('[data-pcc-qs-contents]');
    const optionsWrap = q('[data-pcc-qs-options]');
    const form = q('[data-pcc-qs-form]');
    const add = q('[data-pcc-qs-add]');
    const error = q('[data-pcc-qs-error]');
    const review = q('[data-pcc-qs-review]');
    const link = q('[data-pcc-qs-link]');
    const status = q('[data-pcc-qs-status]');
    const guideButton = q('[data-pcc-qs-guide]');
    const gifts = q('[data-pcc-qs-gifts]'); // "2 FREE GIFTS INCLUDED" label, shown for preset bundles only
    const optionsView = q('[data-pcc-qs-view="options"]');
    const guideView = q('[data-pcc-qs-view="guide"]');
    const labels = sheet.dataset;
    let product = null;
    let variant = null;
    let opener = null;
    let busy = false;

    const sizeIndex = () => (product ? product.options.findIndex((name) => /^size$/i.test(name)) : -1);
    const selectedValues = () =>
      product.options.map((_, i) => {
        const checked = optionsWrap.querySelector(`input[data-index="${i}"]:checked`);
        return checked ? checked.value : null;
      });
    const setButton = (enabled, text) => {
      add.disabled = !enabled;
      add.textContent = text;
    };
    const addLabel = () => (product && product.gifts ? 'ADD BUNDLE + FREE GIFTS' : labels.addLabel);

    const update = () => {
      const selected = selectedValues();
      product.options.forEach((_, i) => {
        const output = optionsWrap.querySelector(`[data-pcc-value="${i}"]`);
        if (output) output.textContent = selected[i] || labels.chooseValue || 'Select';
      });
      error.hidden = true;
      variant = null;
      if (selected.includes(null)) {
        setButton(false, labels.chooseLabel);
        return;
      }
      variant = product.variants.find((v) => v.options.every((value, i) => value === selected[i])) || null;
      if (!variant) {
        setButton(false, 'Unavailable');
        return;
      }
      price.textContent = money(variant.price);
      if (product.preview) {
        setButton(false, labels.previewLabel);
        status.textContent = `${variant.title} selected. ${labels.previewNote}`;
        return;
      }
      if (variant.available) setButton(true, addLabel());
      else setButton(false, labels.soldoutLabel);
      status.textContent = `${variant.title}, ${money(variant.price)}${variant.available ? '' : ', sold out'}`;
    };

    const build = () => {
      optionsWrap.textContent = '';
      const si = sizeIndex();
      product.options.forEach((name, i) => {
        const values = [...new Set(product.variants.map((v) => v.options[i]))];
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'pc-opt';
        const legend = document.createElement('legend');
        legend.className = 'pc-opt__legend';
        legend.append(`${name}: `);
        const output = document.createElement('span');
        output.dataset.pccValue = i;
        legend.append(output);
        fieldset.append(legend);
        const wrap = document.createElement('div');
        wrap.className = 'pc-opt__values';
        values.forEach((value, n) => {
          const id = `${sheet.id}-${i}-${n}`;
          const input = document.createElement('input');
          Object.assign(input, { type: 'radio', className: 'pc-opt__input', id, name: `${sheet.id}-opt-${i}`, value });
          input.dataset.index = i;
          const available = product.variants.some((v) => v.options[i] === value && v.available);
          if (!available) input.disabled = true;
          // Size is always an explicit choice; only a single non-size value is filled in for the shopper.
          if (values.length === 1 && available && i !== si) input.checked = true;
          const label = document.createElement('label');
          label.className = 'pc-opt__label';
          label.htmlFor = id;
          label.textContent = value;
          if (!available) {
            const sr = document.createElement('span');
            sr.className = 'pc-sr';
            sr.textContent = ' (sold out)';
            label.append(sr);
          }
          wrap.append(input, label);
        });
        fieldset.append(wrap);
        optionsWrap.append(fieldset);
      });
    };

    const renderContents = () => {
      if (!contents) return;
      contents.textContent = '';
      const items = Array.isArray(product.contents) ? product.contents : [];
      contents.hidden = !items.length;
      items.forEach((item) => {
        const li = document.createElement('li');
        li.className = 'pc-pack-contents__item';
        const swatch = document.createElement('span');
        swatch.className = 'pc-pack-contents__swatch';
        swatch.setAttribute('aria-hidden', 'true');
        if (item.swatch) swatch.style.setProperty('--pc-swatch', item.swatch);
        const qty = document.createElement('span');
        qty.className = 'pc-pack-contents__qty';
        qty.textContent = `${item.quantity}×`;
        const color = document.createElement('span');
        color.className = 'pc-pack-contents__color';
        color.textContent = item.color;
        li.append(swatch, qty, color);
        contents.append(li);
      });
    };

    const showOptions = (focusGuideButton) => {
      if (guideView) guideView.hidden = true;
      optionsView.hidden = false;
      if (focusGuideButton && guideButton) guideButton.focus({ preventScroll: true });
    };
    const showGuide = () => {
      if (!guideView) return;
      const si = sizeIndex();
      const size = si > -1 ? selectedValues()[si] : '';
      optionsView.hidden = true;
      guideView.hidden = false;
      if (window.PrimeCutSizeGuide && typeof window.PrimeCutSizeGuide.reset === 'function') {
        window.PrimeCutSizeGuide.reset(guideView, size || '');
      }
      sheet.scrollTop = 0;
      const back = guideView.querySelector('[data-pc-sg-back]');
      if (back) back.focus({ preventScroll: true });
    };

    const open = (card, button) => {
      const data = card && card.querySelector('[data-pcc-product]');
      if (!data) return;
      try {
        product = JSON.parse(data.textContent);
      } catch (e) {
        const fallback = card.querySelector('a[href]');
        if (fallback) window.location.assign(fallback.href);
        return;
      }
      opener = button;
      title.textContent = product.title;
      summary.textContent = product.packQty ? `${product.packQty} tees · one size for every tee` : '';
      summary.hidden = !summary.textContent;
      renderContents();
      const prices = product.variants.map((v) => v.price);
      const min = Math.min(...prices);
      price.textContent = (Math.max(...prices) > min ? 'From ' : '') + money(min);
      if (product.image) {
        img.src = product.image;
        img.hidden = false;
      } else img.hidden = true;
      link.href = product.url;
      link.textContent = product.packQty ? labels.packLinkLabel || 'View pack details' : labels.linkLabel || 'View full details';
      if (review) review.hidden = !product.preview;
      if (gifts) gifts.hidden = !product.gifts;
      if (product.gifts && product.url) loadDeal(product.url).catch(() => {}); // warm it while a size is chosen
      if (guideButton) guideButton.hidden = sizeIndex() < 0;
      status.textContent = '';
      build();
      showOptions(false);
      update();
      if (typeof sheet.showModal === 'function') {
        if (!sheet.open) sheet.showModal();
      } else sheet.setAttribute('open', '');
      document.documentElement.classList.add('pc-scroll-lock');
      const first = optionsWrap.querySelector('input:not(:disabled):not(:checked)') || q('[data-pcc-qs-close]');
      if (first) first.focus();
    };

    const close = () => {
      if (typeof sheet.close === 'function' && sheet.open) sheet.close();
      else if (sheet.hasAttribute('open')) {
        sheet.removeAttribute('open');
        sheet.dispatchEvent(new Event('close'));
      }
    };

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-pcc-quick]');
      if (!button || button.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      open(button.closest('[data-pcc-card]'), button);
    });
    q('[data-pcc-qs-close]').addEventListener('click', close);
    if (guideButton) guideButton.addEventListener('click', showGuide);
    if (guideView) {
      guideView.addEventListener('click', (event) => {
        if (event.target.closest('[data-pc-sg-back]')) showOptions(true);
      });
    }
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet) close();
    });
    sheet.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (guideView && !guideView.hidden) showOptions(true);
      else close();
    });
    sheet.addEventListener('cancel', (event) => event.preventDefault());
    sheet.addEventListener('close', () => {
      showOptions(false);
      if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('pc-scroll-lock');
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    });
    optionsWrap.addEventListener('change', update);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (product && product.preview) {
        if (review) review.hidden = false;
        return;
      }
      if (!variant || !variant.available || busy) return;
      busy = true;
      setButton(false, labels.addingLabel);
      error.hidden = true;
      const drawer = document.querySelector('cart-drawer');
      const sections =
        drawer && typeof drawer.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map((s) => s.id) : null;
      const chosen = variant;
      try {
        let body;
        const headers = { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' };
        if (product.gifts) {
          // The bundle and its gifts go in one request, so the gifts can never land without the bundle or vice versa.
          const [data, cart] = await Promise.all([
            loadDeal(product.url),
            fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json()),
          ]);
          const items = buildGiftItems(data, chosen.id, cart);
          if (!items) throw new Error("We couldn't add the free gifts right now. Please try again, or open View pack details.");
          headers['Content-Type'] = 'application/json';
          body = JSON.stringify(sections ? { items, sections: sections.join(','), sections_url: window.location.pathname } : { items });
        } else {
          body = new FormData();
          body.append('id', chosen.id);
          body.append('quantity', '1');
          if (sections) {
            body.append('sections', sections);
            body.append('sections_url', window.location.pathname);
          }
        }
        const response = await fetch(`${rootUrl()}cart/add.js`, { method: 'POST', headers, body });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || labels.errorLabel);
        status.textContent = labels.addedLabel;
        setButton(false, labels.addedLabel);
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pc-collection', productVariantId: chosen.id, cartData: state });
        }
        close();
        if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(state);
        } else {
          window.location.assign(`${rootUrl()}cart`);
        }
      } catch (err) {
        const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
        error.textContent = offline
          ? "We couldn't reach the cart. Check your connection and try again."
          : err && err.message && err.message !== 'Failed to fetch'
            ? err.message
            : labels.errorLabel;
        error.hidden = false;
        setButton(true, addLabel());
      } finally {
        busy = false;
      }
    });
  }

  // After Audit: all black / all white / mixed. Shows and hides the cards already on the page; nothing is fetched and
  // no collection, tag or Search & Discovery setting is involved. The choice rides along in ?colors= so it survives
  // the pack-size pills (plain links) and a reload, and it never touches a product or variant link.
  function initColorFilter(root) {
    const bar = root.querySelector('[data-pcc-colorfilter]');
    if (!bar || bar.dataset.pcReady) return;
    bar.dataset.pcReady = 'true';
    bar.hidden = false;
    const empty = root.querySelector('[data-pcc-color-empty]');
    const status = root.querySelector('[data-pcc-color-status]');
    const count = root.querySelector('.pcc__count');
    const totalText = count ? count.textContent : '';
    const groups = [...bar.querySelectorAll('[data-pcc-color]')].map((btn) => btn.dataset.pccColor);
    const apply = (value, announce) => {
      const group = groups.includes(value) ? value : 'all';
      let shown = 0;
      root.querySelectorAll('.pcc__item').forEach((item) => {
        const card = item.querySelector('[data-pcc-card]');
        const match = group === 'all' || (card && card.dataset.pccColors === group);
        item.hidden = !match;
        if (match) shown += 1;
      });
      root.querySelectorAll('[data-pcc-color]').forEach((btn) => {
        if (btn.closest('[data-pcc-colorfilter]')) btn.setAttribute('aria-pressed', String(btn.dataset.pccColor === group));
      });
      if (empty) empty.hidden = shown > 0;
      if (count) count.textContent = group === 'all' ? totalText : `${shown} ${shown === 1 ? 'product' : 'products'}`;
      if (status && announce) status.textContent = `${shown} ${shown === 1 ? 'pack' : 'packs'} shown.`;
      const url = new URL(window.location.href);
      if (group === 'all') url.searchParams.delete('colors');
      else url.searchParams.set('colors', group);
      window.history.replaceState(window.history.state, '', url.toString());
      root.querySelectorAll('[data-pcc-pill]').forEach((pill) => {
        const target = new URL(pill.getAttribute('href'), window.location.origin);
        if (group === 'all') target.searchParams.delete('colors');
        else target.searchParams.set('colors', group);
        pill.setAttribute('href', target.pathname + target.search);
      });
    };
    root.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-pcc-color]');
      if (!btn) return;
      apply(btn.dataset.pccColor, true);
      if (!btn.closest('[data-pcc-colorfilter]')) {
        const first = bar.querySelector('[aria-pressed="true"]');
        if (first) first.focus();
      }
    });
    apply(new URLSearchParams(window.location.search).get('colors') || 'all', false);
  }

  const init = (scope) => {
    scope.querySelectorAll('[data-pcc]').forEach((root) => {
      initControls(root);
      initSheet(root);
      initColorFilter(root);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
